import React from 'react';
import { cn } from '@/lib/utils';

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  ({ className, children, size = 'lg', ...props }, ref) => {
    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      full: 'max-w-full',
    };

    return (
      <div
        ref={ref}
        className={cn('mx-auto w-full p-4 sm:px-6', sizeClasses[size], className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Container.displayName = 'Container';

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  direction?: 'vertical' | 'horizontal';
}

export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  ({ className, children, spacing = 'md', direction = 'vertical', ...props }, ref) => {
    const spacingClasses = {
      xs: 'space-y-1',
      sm: 'space-y-2',
      md: 'space-y-4',
      lg: 'space-y-6',
      xl: 'space-y-8',
    };

    const directionClasses = {
      vertical: spacingClasses[spacing],
      horizontal: spacingClasses[spacing].replace('space-y-', 'space-x-'),
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex',
          direction === 'horizontal' ? 'flex-row' : 'flex-col',
          directionClasses[direction],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Stack.displayName = 'Stack';
